/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { Notice } from '@/api/notifications';
import { formatDate } from '@/lib/dates';
import { shortDay, toMoment, untilWords } from '@/lib/outOfOffice';

/**
 * What each event is called in the bell.
 *
 * The same headlines the emails use, so a notice and the email about the same
 * thing read alike. The server sends the event's name and nothing more; the
 * words are chosen here.
 */
const HEADLINE: Record<string, string> = {
  needs_approval: 'Waiting for your approval',
  no_longer_approving: 'No longer waiting on you',
  approved: 'Approved',
  rejected: 'Rejected',
  approver_changed: 'Approver changed',
  no_longer_signing: 'No longer waiting on your signature',
  signer_changed: 'Signer changed',
  // Named for the new signer; see `happened`.
  signer_changed_before_sending: "Amida's signer changed",
  withdrawn: 'Withdrawn from signature',
  sign_again: 'You will be asked to sign again',
  withdrawal_refused: 'Not withdrawn',
  withdrawal_overtaken: 'Signed before it was withdrawn',
  sent_for_signature: 'Out for signature',
  // Amida's signer, once the request reaches them (`NOT-27`).
  needs_signature: 'Your turn to sign',
  signed: 'Signed',
  declined: 'Declined',
  signature_failed: 'Signature request failed',
  signer_bounced: "A signer's email bounced",
  filed_copy_missing: 'A filed document is missing',
  expiring_soon: 'Expiring soon',
  packet_step_done: 'Packet step done',
  packet_handed_over: 'Assigned to you',
  approval_resent: 'Sent again: waiting for your approval',
  // Named for whoever sent it; see `noticeHeadline`.
  approval_reminder: 'Vellum reminded you',
  // Vellum's own reminders, to whoever is next (`REM-6`).
  send_reminder: 'Reminder: ready to send',
  packet_step_reminder: 'Reminder: the next step is waiting',
  out_of_office_set: 'Out of office set',
  out_of_office_asked: 'Asked to cover',
  out_of_office_moved: 'Your work has moved while you are away',
  out_of_office_covering: 'Now covering',
  out_of_office_returned: 'Back with you',
  out_of_office_handed_back: 'Handed back',
  out_of_office_stuck: 'Waiting on somebody who is away',
  out_of_office_not_returned: 'Still with the colleague who covered',
  // An edit cleared their approval; they will be asked again (`LC-18`).
  approval_cleared: 'Your approval was cleared',
  // Named for who has left; see `happened` (`LC-05`).
  approver_inactive: 'An approver has left',
  // To administrators: somebody switched off still owns unsigned agreements
  // (`NT-03`). Named for who has left; see `happened`.
  owner_inactive: 'Somebody who has left still owns agreements',
  // A colleague tagged the reader in a comment: who and where, never what
  // they wrote (`COM-10`). Named for who; see `happened`.
  mentioned: 'You were mentioned in a comment',
};

/** The first half of a headline: what happened, naming somebody where it helps. */
function happened(notice: Notice, now: Date): string {
  const who = notice.actor_name;
  if (notice.event === 'rejected' && who) return `Rejected by ${who}`;
  // Who has left, so the owner knows whom to replace (`LC-05`).
  if (notice.event === 'approver_inactive' && who) return `${who} can no longer approve`;
  // Who has left, so an administrator knows whose work to hand on (`NT-03`).
  if (notice.event === 'owner_inactive' && who) {
    return `${who} has left and still owns agreements`;
  }
  // Who mentioned the reader; never a word of the comment (`COM-10`).
  if (notice.event === 'mentioned' && who) return `${who} mentioned you in a comment`;
  // A reminder names who sent it; none named was Vellum's own (`REM-6`).
  if (notice.event === 'approval_reminder' && who) return `${who} reminded you`;
  // A colleague covering is asked in the absent person's place (`OOO-10`).
  if (notice.event === 'needs_approval' && who) {
    return `Waiting for your approval, covering for ${who}`;
  }
  // Before sending, nobody has been asked yet: it is for when it is sent (§3.6).
  if (notice.event === 'signer_changed_before_sending' && who) {
    const covering = notice.covering_for_name
      ? `, covering for ${notice.covering_for_name}`
      : '';
    return `Amida's signer is now ${who}${covering}, for when it is sent`;
  }
  if (notice.event === 'out_of_office_stuck' && who) {
    // "until 2:30 pm" later today, "until 17 Oct" otherwise: the server's rule.
    const back = toMoment(notice.away_until);
    return back
      ? `Waiting on ${who}, who is away until ${untilWords(back, now)}`
      : `Waiting on ${who}, who is away`;
  }
  return HEADLINE[notice.event] ?? 'Update';
}

/**
 * "Approved — Master services agreement".
 *
 * An out-of-office notice is about a period rather than an agreement, and its
 * title may be empty: the headline then stands alone, with no dash trailing.
 */
export function noticeHeadline(notice: Notice, now: Date = new Date()): string {
  const what = happened(notice, now);
  return notice.title ? `${what} — ${notice.title}` : what;
}

/**
 * Why work waits, where the headline alone would leave the reader asking:
 * a signature already sent stays with somebody whose absence is short
 * (`OOO-D6`). Never anything from the document (`INT-6`).
 */
export function noticeWhy(notice: Notice): string | null {
  if (notice.stays_because === 'short_period') {
    return 'Already sent for signature, and the absence is short, so it waits for them.';
  }
  return null;
}

/**
 * Where opening a notice goes: its agreement, through that page's own
 * permission checks (`NOT-17`); for an out-of-office notice with no agreement,
 * the Out of office screen; or nowhere, when it can no longer be opened.
 */
export type NoticeDestination =
  | { to: '/agreements/$agreementId'; params: { agreementId: string } }
  | { to: '/settings/out-of-office' }
  | { to: '/settings/people' }
  | { to: '/agreements'; search: { kind: 'packet' } }
  | null;

export function noticeDestination(notice: Notice): NoticeDestination {
  if (notice.agreement_id) {
    return notice.can_open
      ? { to: '/agreements/$agreementId', params: { agreementId: notice.agreement_id } }
      : null;
  }
  if (notice.event.startsWith('out_of_office')) return { to: '/settings/out-of-office' };
  // About a person, not an agreement: People, where an administrator hands
  // what they own to a colleague (`NT-03`).
  if (notice.event === 'owner_inactive') return { to: '/settings/people' };
  // A packet handed over names no agreement: its new owner finds it among the
  // packets in progress, on Agreements, which are theirs now (`PKT-36`).
  if (notice.event === 'packet_handed_over') {
    return { to: '/agreements', search: { kind: 'packet' } };
  }
  return null;
}

/** What a reminder says has waited, before "since 2 Oct" (`REM-4`, `REM-6`). */
const WAITING: Record<string, string> = {
  approval_reminder: 'waiting for your approval',
  send_reminder: 'ready for you to send',
  packet_step_reminder: 'next step waiting',
};

/**
 * "VL-1829 · Kestrel Data Labs", leaving out whichever half is missing; for a
 * reminder, how long it has waited too: "· waiting for your approval since
 * 2 Oct", as the email says it (`REM-4`, `REM-6`).
 */
export function noticeSubject(notice: Notice): string {
  const waiting = WAITING[notice.event];
  const since =
    waiting && notice.since
      ? `${waiting} since ${shortDay(notice.since, notice.created_at.slice(0, 10))}`
      : '';
  return [notice.reference, notice.counterparty_name, since].filter(Boolean).join(' · ');
}

const RELATIVE = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

/** "just now", "5 minutes ago", "yesterday", or the date once it is a week old. */
export function noticeWhen(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return RELATIVE.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return RELATIVE.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (days < 7) return RELATIVE.format(-days, 'day');
  return formatDate(iso);
}
